import { PatientProfile, Task, TaskType } from "src/api/types";
import UnreadMessageTask from "../UnreadMessageTask";
import { useClinician } from "src/context/SessionProvider/ClinicianProvider";
import { useUpdateTaskMutation } from "src/api/services/tasksApi";
import { useCallback } from "react";
import IdVerificationTask from "../IdVerificationTask";
import SendMessageTask from "../SendMessageTask";
import PrescriptionRequestTask from "../PrescriptionRequestTask";
import PrescriptionReviewTask from "../PrescriptionReviewTask";
import SubmitOrderTask from "../SubmitOrderTask";
import AddTrackingTask from "../AddTrackingTask";
import PriorAuthTask from "../PriorAuthTask";
import SyncVisitRequestTask from "src/components/SyncVisitRequestTask";
import EPrescriptionRequestTask from "../EPrescriptionRequestTask";
import UnreadTextMessageTask from "src/components/UnreadTextMessageTask";

interface Props {
  task: Task;
  patient: PatientProfile;
}

const TaskView = ({ task, patient }: Props) => {
  const providerId = useClinician((p) => p.id);
  const [updateTask] = useUpdateTaskMutation();

  const onSuccess = useCallback(async () => {
    updateTask({
      id: task.id,
      body: {
        assignee_id: providerId,
        assignee_type: "providers",
        completed_at: new Date().toISOString(),
        action_taken: "COMPLETED",
      },
    }).unwrap();
  }, [providerId, task.id, updateTask]);

  if (task.task_type === "UNREAD_TEXT_MESSAGE") {
    return (
      <UnreadTextMessageTask
        task={task}
        onSuccess={onSuccess}
        patient={patient}
      />
    );
  }

  if (task.task_type === "SYNC_VISIT_REQUEST") {
    return <SyncVisitRequestTask task={task} onSuccess={onSuccess} />;
  }

  if (task.task_type === TaskType.IdVerification) {
    return (
      <IdVerificationTask
        task={task}
        onSuccess={onSuccess}
        profileId={patient.profile_id}
      />
    );
  }

  if (task.task_type === TaskType.UnreadMessage) {
    return <UnreadMessageTask task={task} onSuccess={onSuccess} />;
  }

  if (task.task_type === TaskType.PrescriptionRequest) {
    return (
      <PrescriptionRequestTask
        task={task}
        patient={patient}
        onSuccess={onSuccess}
      />
    );
  }

  if (task.task_type === TaskType.SendMessage) {
    return <SendMessageTask task={task} onSuccess={onSuccess} />;
  }

  if (task.task_type === TaskType.PrescriptionReview) {
    return (
      <PrescriptionReviewTask
        task={task}
        patient={patient}
        onSuccess={onSuccess}
      />
    );
  }

  if (task.task_type === TaskType.SubmitOrder) {
    return <SubmitOrderTask task={task} onSuccess={onSuccess} />;
  }

  if (task.task_type === TaskType.AddTracking) {
    return <AddTrackingTask task={task} onSuccess={onSuccess} />;
  }

  if (
    [
      TaskType.PriorAuthRequest,
      TaskType.PriorAuthApproved,
      TaskType.PriorAuthDenied,
      TaskType.PriorAuthUpdate,
      TaskType.PriorAuthClosed,
      TaskType.PriorAuthCanceled,
    ].includes(task.task_type as TaskType)
  ) {
    return (
      <PriorAuthTask task={task} onSuccess={onSuccess} patient={patient} />
    );
  }

  if (task.task_type === TaskType.EPrescriptionRequest) {
    return (
      <EPrescriptionRequestTask
        task={task}
        onSuccess={onSuccess}
        patient={patient}
      />
    );
  }
};

export default TaskView;
